import { ref } from 'vue'

const CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes
const BUILD_VERSION = import.meta.env.VITE_APP_VERSION as string

let intervalId: ReturnType<typeof setInterval> | null = null
let timeoutId: ReturnType<typeof setTimeout> | null = null
let isRunning = false

// Shared reactive state — singleton across all component instances
const updateAvailable = ref(false)

/**
 * Periodically fetches /version.json and compares the deployed version
 * against the version baked into this JS bundle at build time.
 * When a mismatch is detected, sets updateAvailable to true.
 * Singleton — only one polling interval runs at a time.
 */
export function useVersionCheck() {
  async function check() {
    if (!import.meta.env.PROD) return
    if (updateAvailable.value) return

    try {
      const res = await fetch(`/version.json?_=${Date.now()}`)
      if (!res.ok) return

      const { version } = await res.json()

      if (version && version !== BUILD_VERSION) {
        updateAvailable.value = true
        stop()
      }
    } catch {
      // Network error — skip silently
    }
  }

  function start() {
    if (isRunning) return
    isRunning = true

    intervalId = setInterval(check, CHECK_INTERVAL)

    // First check after a short delay to avoid blocking initial page load
    timeoutId = setTimeout(check, 10_000)
  }

  function stop() {
    if (intervalId) { clearInterval(intervalId); intervalId = null }
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null }
    isRunning = false
  }

  return { updateAvailable, start, stop }
}
